import React, { useState } from 'react';
import { Box, SxProps, Stack, Tooltip } from '@mui/material';
import { CampaignStat } from './components/CampaignStat';
import { Header } from 'components/Header';
import { Paths } from 'services/navigation/paths';
import { CampaignMetaData } from '../components/CampaignMetaData';
import { ConversationTable } from 'modules/conversations/components/ConversationTable';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  CampaignStatus,
  Campaign,
  CampaignType
} from 'services/campaign/types';
import { useSubscriber } from 'services/api/hooks';
import {
  onUpdateCampaign,
  onUpdateCampaignStats
} from 'services/campaign/subsribtions';
import { Loading } from 'components/Loading';
import { useCampaign } from 'services/campaign/hooks';
import { LaunchCampaignModal } from './components/LaunchCampaignModal';
import { ScheduleLaunchModal } from './components/ScheduleLaunchModal';
import { onboardingElementIds } from 'services/onboarding/constants';
import { BackButton } from 'components/Buttons';
import { organizationAtom } from 'services/organization/atom';
import { useRecoilValue } from 'recoil';
import { userAtom } from 'services/user/atoms';
import { useCampaignStats } from 'pages/Campaigns/hooks/useCampaignStats';
import { sanitizeObject } from 'utils/fomatting';
import { SearchInput } from 'components/Inputs/SearchInput';
import { SubscribersModal } from 'modules/campaigns/components/SubscribersModal';
import {
  useGetCampaignSubscribersQuery,
  useGetUsersForOrganizationQuery,
  useUpdateCampaignMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { replaceListOwnUserName } from 'core/utils/replaceListOwnUserName';
import type { UserOrganizationSubscriptionItem } from 'modules/campaigns/types/UserOrganizationSubscriptionItem';
import { useDrawerV2 } from 'common/drawer/hooks/useDrawerV2';
import { EditCampaignDetailsDrawer } from 'modules/campaigns/components/EditCampaignDetailsDrawer';
import { useSearchWithSearchParamEffect } from 'hooks/searchParams/useSearchWithSearchParamEffect';
import { CampaignSearchKeys } from 'pages/Campaigns/constants/searchKeys';
import { ToastType, useToast } from 'services/toast';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import {
  externalIdFields,
  externalIdSchema,
  externalIdSchemaOptional
} from 'common/form/validationSchema/externalId';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';
import {
  layoutContainerPaddingBottom,
  layoutContainerPaddingTop,
  layoutContainerPaddingX
} from 'services/navigation/components/PrivateRoute';
import { ActionHeader } from './components/ActionHeader';
import { CampaignQuestionsDrawer } from 'modules/campaigns/components/CampaignQuestionsDrawer';

const container: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  pt: layoutContainerPaddingTop,
  pb: layoutContainerPaddingBottom,
  px: layoutContainerPaddingX
};

const loadingContainer: SxProps = {
  mt: 3
};

const titleContainer: SxProps = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%'
};

const metaDataContainer: SxProps = {
  display: 'flex',
  justifyContent: 'space-between'
};

const statsContainer: SxProps = {
  mt: 3,
  mb: '40px'
};

const searchActionContainer: SxProps = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between'
};

const tableContainer: SxProps = {
  mt: 3
};

export const CampaignPage: React.FC = () => {
  const { id: campaignId } = useParams();
  const organization = useRecoilValue(organizationAtom);
  const [isLaunchModalOpen, setIsLaunchModalOpen] = useState(false);
  const [isLaunchNowConfirmModalOpen, setIsLaunchNowConfirmModalOpen] =
    useState(false);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [isSubscribersModalOpen, setIsSubscribersModalOpen] = useState(false);

  const currentUser = useRecoilValue(userAtom);
  const { openDrawer } = useDrawerV2();
  const { openModal } = useModalV2();
  const { showToast } = useToast();

  const isExternalIdRequiredFlag = useFlag(Flags.EXTERNAL_ID_IS_MANDATORY);

  const [searchParams] = useSearchParams();

  const [searchName, setSearchName] = useState(
    searchParams.get(CampaignSearchKeys.searchName) ?? ''
  );

  const [updateCampaignMutation] = useUpdateCampaignMutation();

  useSearchWithSearchParamEffect({
    searchKey: CampaignSearchKeys.searchName,
    searchText: searchName
  });

  const organizationId = organization?.id ?? '';

  const {
    data: campaignSubscribersData,
    loading: isCampaignSubscribersLoading,
    refetch: refetchCampaignSubscribers
  } = useGetCampaignSubscribersQuery({
    variables: {
      campaignId: campaignId || ''
    }
  });

  const {
    data: orgMembersData,
    loading: isOrgMembersLoading,
    refetch: refetchOrgMembers
  } = useGetUsersForOrganizationQuery({
    variables: { organizationId },
    skip: !organizationId
  });

  const campaignSubscribersItems = extractQueryListItems(
    campaignSubscribersData
  );
  const orgMembers = orgMembersData?.getUsersForOrganization?.items ?? [];

  const subscriberByUserId = new Map(
    campaignSubscribersItems
      .filter((item) => item.campaignSubscriptionId)
      .map((item) => [item.userId, item.campaignSubscriptionId])
  );

  const userOrganizations: UserOrganizationSubscriptionItem[] =
    replaceListOwnUserName(
      orgMembers.map((member) => {
        const subscriptionId = subscriberByUserId.get(member.id);
        return {
          id: member.id,
          user: {
            id: member.id,
            name: member.name ?? null,
            email: member.email ?? '',
            campaignSubscriptions: subscriptionId
              ? { items: [{ id: subscriptionId }] }
              : null
          }
        };
      }),
      currentUser?.id || ''
    );

  const isUsersWithCampaignSubscriptionLoading =
    isCampaignSubscribersLoading || isOrgMembersLoading;

  const refetchUserOrganizationData = () => {
    refetchCampaignSubscribers();
    refetchOrgMembers();
  };

  const { campaign, setCampaign, isLoading, launchCampaign, isLaunching } =
    useCampaign({
      campaignId: campaignId || '',
      onLaunchCampaignSuccess: () => {
        setCampaign((campaign) =>
          campaign
            ? {
                ...campaign,
                status: CampaignStatus.SENDING_MESSAGES
              }
            : campaign
        );
      }
    });

  const { error: updateCampaignError } = useSubscriber({
    subQuery: onUpdateCampaign,
    subParams: {
      id: { eq: campaignId || '' }
    },
    onFetchSuccess: async (data) => {
      const campaignUpdated: CampaignType = data?.onUpdateCampaign;
      setCampaign((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          ...sanitizeObject(campaignUpdated)
        };
      });
    }
  });

  const { error: updateCampaignStatsError } = useSubscriber({
    subQuery: onUpdateCampaignStats,
    subParams: {
      id: campaignId || ''
    },
    onFetchSuccess: async (data) => {
      const campaignUpdated: Campaign = data?.onUpdateCampaignStats;

      setCampaign((campaign) => ({
        ...campaign,
        ...campaignUpdated
      }));
    }
  });

  const { stats, isLoading: isStatsLoading } = useCampaignStats(campaign);

  if (isLoading)
    return (
      <Box sx={loadingContainer}>
        <Loading />
      </Box>
    );

  if (!campaignId || !campaign) return null;

  const {
    totalProspectsCount,
    jobDescriptionParsed,
    status,
    candidateBatchId,
    launchScheduleDateTime,
    messagesProcessedCount,
    user
  } = campaign;

  const openSubscribersModal = () => {
    setIsSubscribersModalOpen(true);
  };

  const isCampaignOwner = currentUser?.id === campaign.userId;

  const handleEditExternalId = () => {
    openModal({
      title: 'Edit external ID',
      form: {
        schema: isExternalIdRequiredFlag
          ? externalIdSchema
          : externalIdSchemaOptional,
        fields: externalIdFields,
        defaultValues: {
          externalId: campaign.externalId ?? ''
        }
      },
      onPrimaryAction: async (values) => {
        await updateCampaignMutation({
          variables: {
            input: {
              id: campaignId,
              externalId: values.externalId || null
            }
          }
        });

        showToast({
          title: 'External ID updated',
          type: ToastType.Success
        });
      }
    });
  };

  const handleEditCampaignDetails = () => {
    const isEngagementOutreach =
      campaign.type === CampaignType.ENGAGEMENT_OUTREACH;

    openDrawer({
      title: isEngagementOutreach
        ? 'Edit campaign details'
        : 'Edit job details',
      subtitle:
        'Popp will use any new or modified information for future conversations.',
      width: '450px',
      children: <EditCampaignDetailsDrawer campaign={campaign} />
    });
  };

  return (
    <Box sx={container}>
      <Header
        title={
          <Tooltip title={campaign.title || ''}>
            <Box sx={titleContainer}>{campaign.title}</Box>
          </Tooltip>
        }
        prevPagePath={Paths.CAMPAIGNS}
        prevPageTitle="CAMPAIGNS"
        currentPageTitle={campaign.title || 'Campaign'}
      />

      <Box
        sx={metaDataContainer}
        id={onboardingElementIds.createCampaignPageDashboard}
      >
        <CampaignMetaData
          status={status}
          externalId={campaign.externalId}
          candidateBatchId={candidateBatchId}
          launchScheduleDateTime={launchScheduleDateTime}
          totalProspectsCount={totalProspectsCount}
          messagesProcessedCount={messagesProcessedCount}
          channel={campaign.channel}
          location={jobDescriptionParsed?.location}
          userName={user?.name || ''}
          userOrganizations={userOrganizations}
          openSubscribersModal={openSubscribersModal}
          isCampaignOwner={isCampaignOwner}
          hasSubscriptionError={updateCampaignStatsError || updateCampaignError}
          onReschedule={() => setIsRescheduleModalOpen(true)}
        />
      </Box>

      <BackButton goTo={Paths.CAMPAIGNS} />

      <Stack direction="row" gap="17px" flex={1} sx={statsContainer}>
        <Stack direction="row" alignItems="flex-start" flex={1}>
          {stats.map(({ value, type }, index) => {
            return (
              <CampaignStat
                value={value}
                type={type}
                index={index}
                key={type}
                isLoading={isStatsLoading}
              />
            );
          })}
        </Stack>
      </Stack>

      <Box sx={searchActionContainer}>
        <Box>
          <SearchInput
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onClear={() => setSearchName('')}
            placeholder="Search candidates"
          />
        </Box>

        <ActionHeader
          campaign={campaign}
          openEditExternalId={handleEditExternalId}
          openEditCampaignDetails={handleEditCampaignDetails}
          openSubscribersModal={openSubscribersModal}
          openViewQuestions={() =>
            openDrawer({
              title: 'View questions',
              width: '479px',
              children: <CampaignQuestionsDrawer campaign={campaign} />
            })
          }
          onLaunchCampaign={() => setIsLaunchModalOpen(true)}
          onLaunchNow={() => setIsLaunchNowConfirmModalOpen(true)}
          onRescheduleLaunch={() => setIsRescheduleModalOpen(true)}
          isLaunching={isLaunching}
        />
      </Box>

      <Box sx={tableContainer}>
        <ConversationTable
          campaign={campaign}
          searchName={searchName}
          jobTitle={jobDescriptionParsed?.jobTitle || 'N/A'}
          location={jobDescriptionParsed?.location || 'N/A'}
        />
      </Box>

      <LaunchCampaignModal
        isOpen={isLaunchModalOpen}
        onClose={() => setIsLaunchModalOpen(false)}
        onLaunchCampaign={(launchDateTime) =>
          launchCampaign({
            launchScheduleDateTime: launchDateTime
          })
        }
      />

      {/* Reschedule modal */}
      <ScheduleLaunchModal
        isOpen={isRescheduleModalOpen}
        onClose={() => setIsRescheduleModalOpen(false)}
        onConfirm={(scheduledDateTime) =>
          launchCampaign({ launchScheduleDateTime: scheduledDateTime })
        }
      />

      {/* Launch now confirmation modal (for scheduled campaigns) */}
      <LaunchCampaignModal
        isOpen={isLaunchNowConfirmModalOpen}
        onClose={() => setIsLaunchNowConfirmModalOpen(false)}
        onLaunchCampaign={(launchDateTime) =>
          launchCampaign({ launchScheduleDateTime: launchDateTime })
        }
        confirmOnly
      />

      <SubscribersModal
        modalProps={{
          open: isSubscribersModalOpen,
          onClose: () => setIsSubscribersModalOpen(false)
        }}
        isUsersWithCampaignSubscriptionLoading={
          isUsersWithCampaignSubscriptionLoading
        }
        userOrganizations={userOrganizations}
        campaignId={campaign.id}
        organizationId={organization?.id || ''}
        refetchUserOrganizationData={refetchUserOrganizationData}
      />
    </Box>
  );
};
